import React from 'react'
import { useAppSelector } from '../../hooks/redux'

import ErrorToast from '../../components/molecules/ErrorToast'

import { arrayToObject } from '../../utilities'
import {
  deleteIntegration,
  getIntegration,
  getIntegrations,
  postIntegration,
  putIntegration
} from '../../lib/api'
import { selectAccess } from '../../slices/accessSlice'
import useAccess, { PERMS } from '../../hooks/useAccess'
import { useAuth } from '../AuthProvider'

const IntegrationsContext = React.createContext(null)

const IntegrationsProvider = ({ children }) => {
  const { loaded: accessLoaded } = useAppSelector(selectAccess)

  const { isLoggedIn } = useAuth()
  const hasPermission = useAccess()

  const [loaded, setLoaded] = React.useState(false)
  const [integrations, setIntegrations] = React.useState({})
  const [error, setError] = React.useState(null)

  // Loads all integrations initially
  React.useEffect(() => {
    if (loaded || !isLoggedIn || !accessLoaded) {
      return
    }

    if (
      !hasPermission(PERMS.SettingsAdmin) ||
      !hasPermission(PERMS.IntegrationsAdmin)
    ) {
      setLoaded(true)
      return
    }

    getIntegrations()
      .then(ints => {
        setIntegrations(arrayToObject(ints))
        setLoaded(true)
      })
      .catch(err => setError({ err, auto: true }))
  }, [loaded, isLoggedIn, accessLoaded, hasPermission])

  // Triggers refetch
  const reload = React.useCallback(() => {
    setLoaded(false)
  }, [])

  // Updates the entries with a new one
  const update = React.useCallback(data => {
    setIntegrations(old => ({ ...old, [data.id]: data }))
  }, [])

  // Create an integration
  const postInt = React.useCallback(
    async data => {
      try {
        const int = await postIntegration(data)
        int.created_at = new Date().toISOString()
        update(int)
        return int
      } catch (err) {
        setError({ err })
      }
    },
    [update]
  )

  // Gets a single integration by id
  const getInt = React.useCallback(
    async id => {
      try {
        const int = await getIntegration(id)
        update(int)
        return int
      } catch (err) {
        setError({ err })
      }
    },
    [update]
  )

  // Update integration
  const putInt = React.useCallback(
    async (id, data) => {
      try {
        const int = await putIntegration(id, data)
        update(int)
        return int
      } catch (err) {
        setError({ err })
      }
    },
    [update]
  )

  // Delete integration
  const deleteInt = React.useCallback(async id => {
    try {
      await deleteIntegration(id)
      setIntegrations(old => {
        const copy = { ...old }
        delete copy[id]
        return copy
      })
    } catch (err) {
      setError({ err })
    }
  }, [])

  return (
    <>
      <IntegrationsContext.Provider
        value={{
          loaded,
          integrations,
          reload,
          postInt,
          getInt,
          putInt,
          deleteInt,
          setError,
          error
        }}
      >
        {children}
      </IntegrationsContext.Provider>

      <ErrorToast error={error} />
    </>
  )
}

export default IntegrationsProvider

export const useIntegrations = () => React.useContext(IntegrationsContext)
